import { useEffect, useState, useRef, useCallback } from "react";
import { dashboardFirstPaint } from "@/lib/frontend-telemetry";
import { useLocation } from "react-router-dom";
import {
  MessageSquare,
  Users,
  Package,
  Bot,
  TrendingUp,
  ArrowUpRight,
  Activity,
  Sparkles,
  Tags,
  MessageCircle,
  CalendarIcon,
  ClipboardCheck,
  CheckCircle,
  PhoneCall,
  Clock,
  BarChart3,
  UserCheck,
} from "lucide-react";
import { ChannelStatusIndicators } from "@/components/dashboard/ChannelStatusIndicators";
import { DashboardCardActions } from "@/components/dashboard/DashboardCardActions";
import { DASHBOARD_CHART_INFO, DASHBOARD_KPI_INFO } from "@/components/dashboard/dashboardCardInfoTexts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import React from "react";

// Lazy-load chart components
const LeadsStageChart = React.lazy(() =>
  import("@/components/dashboard/LeadsStageChart").then((m) => ({ default: m.LeadsStageChart })),
);
const LeadsTimelineChart = React.lazy(() =>
  import("@/components/dashboard/LeadsTimelineChart").then((m) => ({ default: m.LeadsTimelineChart })),
);
const LeadsByChannelTimelineChart = React.lazy(() =>
  import("@/components/dashboard/LeadsByChannelTimelineChart").then((m) => ({
    default: m.LeadsByChannelTimelineChart,
  })),
);
const LeadsTagsChart = React.lazy(() =>
  import("@/components/dashboard/LeadsTagsChart").then((m) => ({ default: m.LeadsTagsChart })),
);
const ConversationsChannelChart = React.lazy(() =>
  import("@/components/dashboard/ConversationsChannelChart").then((m) => ({ default: m.ConversationsChannelChart })),
);
const LeadsByMemberChart = React.lazy(() =>
  import("@/components/dashboard/LeadsByMemberChart").then((m) => ({ default: m.LeadsByMemberChart })),
);
const MemberPerformanceChart = React.lazy(() =>
  import("@/components/dashboard/MemberPerformanceChart").then((m) => ({ default: m.MemberPerformanceChart })),
);
const AvgServiceTimeChart = React.lazy(() =>
  import("@/components/dashboard/AvgServiceTimeChart").then((m) => ({ default: m.AvgServiceTimeChart })),
);
const PeakHoursChart = React.lazy(() =>
  import("@/components/dashboard/PeakHoursChart").then((m) => ({ default: m.PeakHoursChart })),
);
const ActiveByMemberChart = React.lazy(() =>
  import("@/components/dashboard/ActiveByMemberChart").then((m) => ({ default: m.ActiveByMemberChart })),
);
const TaskCompletionByMemberChart = React.lazy(() =>
  import("@/components/dashboard/TaskCompletionByMemberChart").then((m) => ({ default: m.TaskCompletionByMemberChart })),
);

const ChartFallback = () => (
  <div className="h-[200px] flex items-center justify-center">
    <div className="w-8 h-8 rounded-full bg-primary/20 animate-pulse" />
  </div>
);

// Stagger between revealing each chart when user clicks "Atualize as métricas" (evita pico no banco)
const CHART_REVEAL_STAGGER_MS = 500;
const MAX_CHART_SLOTS = 11;

type KpiKey = "leads" | "products" | "agents" | "conversations" | "closed" | "active";
type ChartKey =
  | "leadsStage"
  | "leadsTimeline"
  | "leadsByChannelTimeline"
  | "leadsTags"
  | "conversationsChannel"
  | "leadsByMember"
  | "memberPerformance"
  | "avgServiceTime"
  | "peakHours"
  | "activeByMember"
  | "tasksByMember";

const CHART_REVEAL_INDEX: Record<ChartKey, number> = {
  leadsStage: 0,
  leadsTimeline: 1,
  leadsByChannelTimeline: 2,
  leadsTags: 3,
  conversationsChannel: 4,
  leadsByMember: 5,
  memberPerformance: 6,
  avgServiceTime: 7,
  peakHours: 8,
  activeByMember: 9,
  tasksByMember: 10,
};

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { workspaceId, workspaceName, loading: workspaceLoading } = useWorkspace(user?.id);
  const { isAdmin } = useMemberPermissions();
  const { toast } = useToast();
  const location = useLocation();
  const [leadsCount, setLeadsCount] = useState(0);
  const [productsCount, setProductsCount] = useState(0);
  const [agentsCount, setAgentsCount] = useState(0);
  const [conversationsCount, setConversationsCount] = useState(0);
  const [closedCount, setClosedCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [hasTagsWithLeads, setHasTagsWithLeads] = useState(false);
  const [hasConversations, setHasConversations] = useState(false);
  const [leadsTimelineRange, setLeadsTimelineRange] = useState<"today" | "7days" | "15days" | "custom">("7days");
  const [leadsByChannelRange, setLeadsByChannelRange] = useState<"today" | "7days" | "15days" | "custom">("7days");
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const [leadsByChannelCustomRange, setLeadsByChannelCustomRange] = useState<DateRange | undefined>();
  const dashboardT0 = useRef(performance.now());
  const firstPaintReported = useRef(false);
  // Gráficos só carregam após o usuário clicar em "Atualize as métricas", em ordem, para não sobrecarregar o banco
  const [chartsEnabled, setChartsEnabled] = useState(false);
  const [chartsRevealIndex, setChartsRevealIndex] = useState(-1);
  const [isRefreshingMetrics, setIsRefreshingMetrics] = useState(false);
  const [kpiRefreshing, setKpiRefreshing] = useState<Partial<Record<KpiKey, boolean>>>({});
  const [chartRefreshing, setChartRefreshing] = useState<Partial<Record<ChartKey, boolean>>>({});
  const [individuallyLoadedCharts, setIndividuallyLoadedCharts] = useState<Set<ChartKey>>(() => new Set());
  const [chartRefreshKeys, setChartRefreshKeys] = useState<Partial<Record<ChartKey, number>>>({});
  const kpiRefreshingRef = useRef<Partial<Record<KpiKey, boolean>>>({});
  const chartRefreshingRef = useRef<Partial<Record<ChartKey, boolean>>>({});

  const fetchStats = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const { data: wsStats, error: wsStatsError } = await (supabase as any)
        .from("workspace_stats")
        .select(
          "leads_count, products_count, agents_count, conversations_count, conversations_closed_count, conversations_active_count, tag_assignments_count",
        )
        .eq("workspace_id", workspaceId)
        .maybeSingle();

      if (!wsStatsError && wsStats) {
        const leads = Number((wsStats as any).leads_count || 0);
        const products = Number((wsStats as any).products_count || 0);
        const agents = Number((wsStats as any).agents_count || 0);
        const convs = Number((wsStats as any).conversations_count || 0);
        const closed = Number((wsStats as any).conversations_closed_count || 0);
        const active = Number((wsStats as any).conversations_active_count || 0);
        const tagsAssigned = Number((wsStats as any).tag_assignments_count || 0);

        setLeadsCount(leads);
        setProductsCount(products);
        setAgentsCount(agents);
        setConversationsCount(convs);
        setClosedCount(closed);
        setActiveCount(active);
        setHasConversations(convs > 0);
        setHasTagsWithLeads(tagsAssigned > 0);
      } else {
        const [leadsRes, productsRes, agentsRes, convsRes, closedRes, activeRes] = await Promise.all([
          supabase.from("leads").select("id", { count: "estimated", head: true }).eq("workspace_id", workspaceId),
          supabase.from("products").select("id", { count: "estimated", head: true }).eq("workspace_id", workspaceId),
          supabase.from("agents").select("id", { count: "estimated", head: true }).eq("workspace_id", workspaceId),
          supabase
            .from("conversations")
            .select("id", { count: "estimated", head: true })
            .eq("workspace_id", workspaceId),
          supabase
            .from("conversations")
            .select("id", { count: "estimated", head: true })
            .eq("workspace_id", workspaceId)
            .eq("is_closed", true),
          supabase
            .from("conversations")
            .select("id", { count: "estimated", head: true })
            .eq("workspace_id", workspaceId)
            .eq("is_closed", false),
        ]);

        if (leadsRes.error) throw leadsRes.error;
        setLeadsCount(leadsRes.count || 0);
        if (productsRes.error) throw productsRes.error;
        setProductsCount(productsRes.count || 0);
        if (agentsRes.error) throw agentsRes.error;
        setAgentsCount(agentsRes.count || 0);
        if (convsRes.error) throw convsRes.error;
        setConversationsCount(convsRes.count || 0);
        setHasConversations((convsRes.count || 0) > 0);
        if (closedRes.error) throw closedRes.error;
        setClosedCount(closedRes.count || 0);
        if (activeRes.error) throw activeRes.error;
        setActiveCount(activeRes.count || 0);
        setHasTagsWithLeads(false);
      }

      if (!firstPaintReported.current) {
        firstPaintReported.current = true;
        dashboardFirstPaint(dashboardT0.current, { workspaceId: workspaceId ?? undefined, route: location.pathname });
      }
    } catch (error: any) {
      console.error("Error fetching workspace data:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do workspace.",
        variant: "destructive",
      });
    }
  }, [workspaceId, toast, location.pathname]);

  const refreshWorkspaceStatsRpc = useCallback(async () => {
    if (!workspaceId) return;
    try {
      await (supabase.rpc as any)("refresh_workspace_stats", { p_workspace_id: workspaceId });
    } catch (_) {
      // ignore — stats will still be fetched below
    }
  }, [workspaceId]);

  const refreshKpi = useCallback(
    async (key: KpiKey) => {
      if (!workspaceId || kpiRefreshingRef.current[key]) return;
      kpiRefreshingRef.current[key] = true;
      setKpiRefreshing((prev) => ({ ...prev, [key]: true }));
      try {
        await refreshWorkspaceStatsRpc();
        await fetchStats();
      } finally {
        kpiRefreshingRef.current[key] = false;
        setKpiRefreshing((prev) => ({ ...prev, [key]: false }));
      }
    },
    [workspaceId, refreshWorkspaceStatsRpc, fetchStats],
  );

  const isChartVisible = useCallback(
    (key: ChartKey) => {
      const revealIdx = CHART_REVEAL_INDEX[key];
      return (chartsEnabled && chartsRevealIndex >= revealIdx) || individuallyLoadedCharts.has(key);
    },
    [chartsEnabled, chartsRevealIndex, individuallyLoadedCharts],
  );

  const refreshChart = useCallback(
    async (key: ChartKey) => {
      if (!workspaceId || chartRefreshingRef.current[key]) return;
      chartRefreshingRef.current[key] = true;
      setChartRefreshing((prev) => ({ ...prev, [key]: true }));
      try {
        setIndividuallyLoadedCharts((prev) => new Set(prev).add(key));
        setChartRefreshKeys((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
      } finally {
        chartRefreshingRef.current[key] = false;
        setChartRefreshing((prev) => ({ ...prev, [key]: false }));
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Stagger: ao ativar gráficos, revelar um a um para não sobrecarregar o banco
  useEffect(() => {
    if (!chartsEnabled) return;
    const id = setInterval(() => {
      setChartsRevealIndex((prev) => {
        const next = prev + 1;
        if (next >= MAX_CHART_SLOTS - 1) {
          setIsRefreshingMetrics(false);
        }
        return Math.min(next, MAX_CHART_SLOTS - 1);
      });
    }, CHART_REVEAL_STAGGER_MS);
    return () => clearInterval(id);
  }, [chartsEnabled]);

  const handleUpdateMetrics = useCallback(async () => {
    if (!workspaceId) return;
    setIsRefreshingMetrics(true);
    try {
      // Refresh stats in DB before fetching (lightweight: 6 COUNTs with indexes)
      try {
        await refreshWorkspaceStatsRpc();
      } catch (_) {
        // ignore — stats will still be fetched below
      }
      await fetchStats();
      if (chartsEnabled) {
        toast({ title: "Métricas atualizadas", description: "Os números do resumo foram atualizados." });
        return;
      }
      setChartsEnabled(true);
      setChartsRevealIndex(0);
    } finally {
      setIsRefreshingMetrics(false);
    }
  }, [workspaceId, chartsEnabled, fetchStats, toast, refreshWorkspaceStatsRpc]);

  const stats: {
    key: KpiKey;
    title: string;
    value: string;
    description: string;
    icon: typeof Users;
    gradient: string;
    info: string;
  }[] = [
    {
      key: "leads",
      title: "Leads",
      value: leadsCount.toString(),
      description: "Total de leads cadastrados",
      icon: Users,
      gradient: "from-blue-500 to-cyan-500",
      info: DASHBOARD_KPI_INFO.leads,
    },
    {
      key: "products",
      title: "Produtos",
      value: productsCount.toString(),
      description: "Produtos no catálogo",
      icon: Package,
      gradient: "from-emerald-500 to-teal-500",
      info: DASHBOARD_KPI_INFO.products,
    },
    {
      key: "agents",
      title: "Agentes IA",
      value: agentsCount.toString(),
      description: "Agentes configurados",
      icon: Bot,
      gradient: "from-violet-500 to-purple-500",
      info: DASHBOARD_KPI_INFO.agents,
    },
    {
      key: "conversations",
      title: "Conversas",
      value: conversationsCount.toString(),
      description: "Total de conversas",
      icon: MessageSquare,
      gradient: "from-orange-500 to-pink-500",
      info: DASHBOARD_KPI_INFO.conversations,
    },
    {
      key: "closed",
      title: "Atendimentos",
      value: closedCount.toString(),
      description: "Total encerrados",
      icon: CheckCircle,
      gradient: "from-green-500 to-emerald-600",
      info: DASHBOARD_KPI_INFO.closed,
    },
    {
      key: "active",
      title: "Ativos",
      value: activeCount.toString(),
      description: "Atendimentos em aberto",
      icon: PhoneCall,
      gradient: "from-amber-500 to-yellow-500",
      info: DASHBOARD_KPI_INFO.active,
    },
  ];

  if (authLoading || workspaceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/50 animate-pulse mx-auto mb-4"></div>
            <Sparkles className="w-6 h-6 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-muted-foreground">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-1 sm:mt-2 text-xs sm:text-sm lg:text-base">
              Visão geral do seu workspace
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <ChannelStatusIndicators workspaceId={workspaceId} />
            <span
              className={
                isRefreshingMetrics
                  ? "inline-flex rounded-full"
                  : "inline-flex rounded-full border-2 border-primary animate-pulse-border-primary"
              }
            >
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpdateMetrics}
              disabled={isRefreshingMetrics}
              className="shrink-0 gap-1.5 rounded-full border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5 px-3 py-2 text-xs font-medium sm:text-sm"
            >
              {isRefreshingMetrics ? (
                <>
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent sm:h-4 sm:w-4" />
                  Atualizando…
                </>
              ) : chartsEnabled ? (
                <>
                  <Activity className="h-3.5 w-3.5 text-primary sm:h-4 sm:w-4" />
                  Atualize as métricas
                </>
              ) : (
                <>
                  <Activity className="h-3.5 w-3.5 text-primary sm:h-4 sm:w-4" />
                  Atualize as métricas
                </>
              )}
            </Button>
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {stats.map((stat) => (
            <Card key={stat.key} className="group hover:shadow-xl transition-all duration-300 border-0 overflow-hidden">
              <CardContent className="p-4 sm:p-5 lg:p-6 relative">
                <DashboardCardActions
                  content={stat.info}
                  onRefresh={() => refreshKpi(stat.key)}
                  isRefreshing={!!kpiRefreshing[stat.key]}
                />
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}
                  >
                    <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-1">{stat.title}</p>
                  <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
                    {stat.value}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-1.5 sm:mt-2">{stat.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Gráficos: só carregam após "Atualize as métricas", em ordem, para não sobrecarregar o banco */}
        {!chartsEnabled && (
          <p className="text-sm text-muted-foreground text-center py-2">
            Clique em <strong>Atualize as métricas</strong> acima para carregar os gráficos.
          </p>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.leadsStage}
                onRefresh={() => refreshChart("leadsStage")}
                isRefreshing={!!chartRefreshing.leadsStage}
              />
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg">Leads por Estágio</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Distribuição dos seus leads</p>
                </div>
              </div>
              {isChartVisible("leadsStage") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <LeadsStageChart workspaceId={workspaceId} refreshKey={chartRefreshKeys.leadsStage ?? 0} />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.leadsTimeline}
                onRefresh={() => refreshChart("leadsTimeline")}
                isRefreshing={!!chartRefreshing.leadsTimeline}
              />
              <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex shrink-0 items-center justify-center shadow-lg">
                  <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-2.5 sm:gap-3 pr-6">
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg leading-snug">Evolução de Leads</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {leadsTimelineRange === "today" && "Leads de hoje"}
                      {leadsTimelineRange === "7days" && "Novos leads nos últimos 7 dias"}
                      {leadsTimelineRange === "15days" && "Novos leads nos últimos 15 dias"}
                      {leadsTimelineRange === "custom" &&
                        customDateRange?.from &&
                        customDateRange?.to &&
                        `${format(customDateRange.from, "dd/MM", { locale: ptBR })} - ${format(customDateRange.to, "dd/MM", { locale: ptBR })}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                  <Button
                    variant={leadsTimelineRange === "today" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLeadsTimelineRange("today")}
                    className="text-xs h-7 px-2"
                  >
                    Hoje
                  </Button>
                  <Button
                    variant={leadsTimelineRange === "7days" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLeadsTimelineRange("7days")}
                    className="text-xs h-7 px-2"
                  >
                    7 dias
                  </Button>
                  <Button
                    variant={leadsTimelineRange === "15days" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLeadsTimelineRange("15days")}
                    className="text-xs h-7 px-2"
                  >
                    15 dias
                  </Button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={leadsTimelineRange === "custom" ? "default" : "outline"}
                        size="sm"
                        className="text-xs h-7 px-2"
                      >
                        <CalendarIcon className="w-3 h-3 mr-1" />
                        Personalizado
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="range"
                        selected={customDateRange}
                        onSelect={(range) => {
                          setCustomDateRange(range);
                          if (range?.from && range?.to) setLeadsTimelineRange("custom");
                        }}
                        initialFocus
                        className="pointer-events-auto"
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                  </div>
                </div>
              </div>
              {isChartVisible("leadsTimeline") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <LeadsTimelineChart
                    workspaceId={workspaceId}
                    dateRange={leadsTimelineRange}
                    customStartDate={customDateRange?.from}
                    customEndDate={customDateRange?.to}
                    refreshKey={chartRefreshKeys.leadsTimeline ?? 0}
                  />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.leadsByChannelTimeline}
                onRefresh={() => refreshChart("leadsByChannelTimeline")}
                isRefreshing={!!chartRefreshing.leadsByChannelTimeline}
              />
              <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex shrink-0 items-center justify-center shadow-lg">
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-2.5 sm:gap-3 pr-6">
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg leading-snug">Leads por Canal</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {leadsByChannelRange === "today" && "Leads de hoje por canal de entrada"}
                      {leadsByChannelRange === "7days" && "Novos leads por canal nos últimos 7 dias"}
                      {leadsByChannelRange === "15days" && "Novos leads por canal nos últimos 15 dias"}
                      {leadsByChannelRange === "custom" &&
                        leadsByChannelCustomRange?.from &&
                        leadsByChannelCustomRange?.to &&
                        `${format(leadsByChannelCustomRange.from, "dd/MM", { locale: ptBR })} - ${format(leadsByChannelCustomRange.to, "dd/MM", { locale: ptBR })}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      variant={leadsByChannelRange === "today" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setLeadsByChannelRange("today")}
                      className="text-xs h-7 px-2"
                    >
                      Hoje
                    </Button>
                    <Button
                      variant={leadsByChannelRange === "7days" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setLeadsByChannelRange("7days")}
                      className="text-xs h-7 px-2"
                    >
                      7 dias
                    </Button>
                    <Button
                      variant={leadsByChannelRange === "15days" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setLeadsByChannelRange("15days")}
                      className="text-xs h-7 px-2"
                    >
                      15 dias
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={leadsByChannelRange === "custom" ? "default" : "outline"}
                          size="sm"
                          className="text-xs h-7 px-2"
                        >
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          Personalizado
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="range"
                          selected={leadsByChannelCustomRange}
                          onSelect={(range) => {
                            setLeadsByChannelCustomRange(range);
                            if (range?.from && range?.to) setLeadsByChannelRange("custom");
                          }}
                          initialFocus
                          className="pointer-events-auto"
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
              {isChartVisible("leadsByChannelTimeline") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <LeadsByChannelTimelineChart
                    workspaceId={workspaceId}
                    dateRange={leadsByChannelRange}
                    customStartDate={leadsByChannelCustomRange?.from}
                    customEndDate={leadsByChannelCustomRange?.to}
                    refreshKey={chartRefreshKeys.leadsByChannelTimeline ?? 0}
                  />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          {hasTagsWithLeads && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-4 sm:p-5 lg:p-6 relative">
                <DashboardCardActions
                  content={DASHBOARD_CHART_INFO.leadsTags}
                  onRefresh={() => refreshChart("leadsTags")}
                  isRefreshing={!!chartRefreshing.leadsTags}
                />
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-lg">
                    <Tags className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">Leads por Tag</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Distribuição das suas tags</p>
                  </div>
                </div>
                {isChartVisible("leadsTags") ? (
                  <React.Suspense fallback={<ChartFallback />}>
                    <LeadsTagsChart workspaceId={workspaceId} refreshKey={chartRefreshKeys.leadsTags ?? 0} />
                  </React.Suspense>
                ) : (
                  <ChartFallback />
                )}
              </CardContent>
            </Card>
          )}

          {hasConversations && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-4 sm:p-5 lg:p-6 relative">
                <DashboardCardActions
                  content={DASHBOARD_CHART_INFO.conversationsChannel}
                  onRefresh={() => refreshChart("conversationsChannel")}
                  isRefreshing={!!chartRefreshing.conversationsChannel}
                />
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">Conversas por Canal</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Distribuição por plataforma</p>
                  </div>
                </div>
                {isChartVisible("conversationsChannel") ? (
                  <React.Suspense fallback={<ChartFallback />}>
                    <ConversationsChannelChart
                      workspaceId={workspaceId}
                      refreshKey={chartRefreshKeys.conversationsChannel ?? 0}
                    />
                  </React.Suspense>
                ) : (
                  <ChartFallback />
                )}
              </CardContent>
            </Card>
          )}

          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.leadsByMember}
                onRefresh={() => refreshChart("leadsByMember")}
                isRefreshing={!!chartRefreshing.leadsByMember}
              />
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg">Leads por Membro</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Distribuição de atendimento</p>
                </div>
              </div>
              {isChartVisible("leadsByMember") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <LeadsByMemberChart workspaceId={workspaceId} refreshKey={chartRefreshKeys.leadsByMember ?? 0} />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.memberPerformance}
                onRefresh={() => refreshChart("memberPerformance")}
                isRefreshing={!!chartRefreshing.memberPerformance}
              />
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <ClipboardCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg">Desempenho por Membro</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Atendimentos encerrados e reabertos</p>
                </div>
              </div>
              {isChartVisible("memberPerformance") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <MemberPerformanceChart
                    workspaceId={workspaceId}
                    refreshKey={chartRefreshKeys.memberPerformance ?? 0}
                  />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.avgServiceTime}
                onRefresh={() => refreshChart("avgServiceTime")}
                isRefreshing={!!chartRefreshing.avgServiceTime}
              />
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg">Tempo Médio de Atendimento</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Duração média por membro</p>
                </div>
              </div>
              {isChartVisible("avgServiceTime") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <AvgServiceTimeChart workspaceId={workspaceId} refreshKey={chartRefreshKeys.avgServiceTime ?? 0} />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.peakHours}
                onRefresh={() => refreshChart("peakHours")}
                isRefreshing={!!chartRefreshing.peakHours}
              />
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-lg">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg">Horários de Pico</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Volume de mensagens por hora</p>
                </div>
              </div>
              {isChartVisible("peakHours") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <PeakHoursChart workspaceId={workspaceId} refreshKey={chartRefreshKeys.peakHours ?? 0} />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg overflow-hidden">
            <CardContent className="p-4 sm:p-5 lg:p-6 relative">
              <DashboardCardActions
                content={DASHBOARD_CHART_INFO.activeByMember}
                onRefresh={() => refreshChart("activeByMember")}
                isRefreshing={!!chartRefreshing.activeByMember}
              />
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg">
                  <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-base sm:text-lg">Ativos por Atendente</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Atendimentos abertos por membro</p>
                </div>
              </div>
              {isChartVisible("activeByMember") ? (
                <React.Suspense fallback={<ChartFallback />}>
                  <ActiveByMemberChart workspaceId={workspaceId} refreshKey={chartRefreshKeys.activeByMember ?? 0} />
                </React.Suspense>
              ) : (
                <ChartFallback />
              )}
            </CardContent>
          </Card>

          {isAdmin && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-4 sm:p-5 lg:p-6 relative">
                <DashboardCardActions
                  content={DASHBOARD_CHART_INFO.tasksByMember}
                  onRefresh={() => refreshChart("tasksByMember")}
                  isRefreshing={!!chartRefreshing.tasksByMember}
                />
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 lg:mb-6">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg">
                    <ClipboardCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg">Tarefas por Membro</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">Conclusão nos últimos 30 dias</p>
                  </div>
                </div>
                {isChartVisible("tasksByMember") ? (
                  <React.Suspense fallback={<ChartFallback />}>
                    <TaskCompletionByMemberChart
                      workspaceId={workspaceId}
                      refreshKey={chartRefreshKeys.tasksByMember ?? 0}
                    />
                  </React.Suspense>
                ) : (
                  <ChartFallback />
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
